import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PosCheckoutPageShell from './PosCheckoutPageShell';
import PosCheckoutSummary from './PosCheckoutSummary';
import { useConfig } from '../context/ConfigContext';
import { useCheckoutPageSession } from '../hooks/useCheckoutPageSession';
import { loadingSpinner } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { formatPlanDuration } from '../utils/subscriptionDateUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import {
  computeApplicationTotal,
  submitSubscriptionApplication,
  subscribeAdminSettings,
  DEFAULT_ADMIN_SETTINGS,
} from '../utils/posSubscriptionUtil';
import { CHECKOUT_EVENTS, CHECKOUT_PAGE_PHASES } from '../constants/bookingConst';

/**
 * A till's subscription checkout, in its own browser tab
 * (/<client-id>/checkout/<session-id>) — the court-booking system's
 * CheckoutPage.
 *
 * Opened by the till's Subscribe dialog (PosSubscribeModal, through
 * hooks/useCheckoutTab), which hands over the plan, the extra staff and who
 * is signed in. This tab takes the payment, SUBMITS the application itself,
 * reports it back and closes — the till then shows the confirmation. × backs
 * out to the till's dialog without sending anything.
 *
 * The QR code and account come from Admin Settings, read live here; the
 * prices are the ones the till showed when checkout was opened, so the total
 * cannot change under the person paying it.
 *
 * @returns {JSX.Element}
 */
function PosSubscribeCheckout() {
  const { sessionId } = useParams();
  const { config, client, clientId, loading: configLoading } = useConfig();
  const { session, phase, endCheckout } = useCheckoutPageSession(sessionId);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => subscribeAdminSettings(
    setSettings,
    () => setSettings(DEFAULT_ADMIN_SETTINGS),
  ), []);

  const order = session?.record || null;
  // An order handed over for a different client than this URL's — a link
  // edited by hand. Shown as inactive rather than paid under the wrong name.
  const foreign = Boolean(order && order.clientId !== clientId);
  const brandName = config?.courtName || clientId;

  if (configLoading || (phase === CHECKOUT_PAGE_PHASES.ACTIVE && !foreign && !settings)) {
    return loadingSpinner('Preparing your checkout', config?.logoUrl);
  }

  /**
   * Sends the application with its receipt, then hands the till back the
   * saved application and closes this tab.
   * @param {File} receiptFile
   * @returns {Promise<void>}
   */
  const handleSubmit = async (receiptFile) => {
    setSubmitting(true);
    setError('');
    try {
      const application = await submitSubscriptionApplication({
        clientId,
        clientName: client?.name || config?.courtName || clientId,
        plan: order.plan,
        extraStaffEmails: order.extraStaffEmails,
        extraStaffPrice: order.extraStaffPrice,
        receiptFile,
        submittedBy: order.submittedBy,
      });
      endCheckout(CHECKOUT_EVENTS.SUBMITTED, { application });
    } catch (failure) {
      console.error('Submitting subscription application failed:', failure);
      setError(failure.isUserFacing
        ? failure.message
        : describeWriteError(failure, { what: 'your application', path: 'posSubscriptionApplications' }));
      setSubmitting(false);
    }
  };

  /** The ×: back to the till's dialog, nothing sent. @returns {void} */
  const handleClose = () => endCheckout(CHECKOUT_EVENTS.CANCELLED);

  /** @returns {JSX.Element|null} the checkout card for the handed-over order */
  const renderCard = () => {
    if (!order || foreign) return null;
    const { plan, extraStaffEmails, extraStaffPrice } = order;
    const { planPrice, freeStaffCount, total } = computeApplicationTotal(plan, extraStaffEmails.length, extraStaffPrice);
    const boxes = [{
      key: 'plan',
      title: plan.name,
      label: formatPlanDuration(plan.durationValue, plan.durationUnit),
      rows: [{ key: 'price', label: 'Subscription', amount: formatMoney(planPrice, config) }],
    }];
    if (extraStaffEmails.length > 0) {
      // The plan's free accounts cover the first addresses; the rest are charged.
      boxes.push({
        key: 'staff',
        title: 'Staff accounts',
        label: freeStaffCount > 0 ? `${freeStaffCount} included with your plan` : '',
        rows: extraStaffEmails.map((email, index) => ({
          key: email,
          label: email,
          amount: index < freeStaffCount ? 'Included' : formatMoney(extraStaffPrice, config),
        })),
      });
    }
    const payTo = [settings.paymentAccountName, settings.paymentAccountNumber].filter(Boolean).join(' · ');

    return (
      <PosCheckoutSummary
        title="Subscription details"
        boxes={boxes}
        total={formatMoney(total, config)}
        instructions={settings.paymentInstructions
          || <>Please pay exactly <strong>{formatMoney(total, config)}</strong>, then upload a clear screenshot of the receipt showing the reference number.</>}
        payTo={payTo}
        qrUrl={settings.paymentQrUrl}
        noQrMessage="The payment QR code has not been set up yet. Please contact The Website Pal for payment details."
        submitLabel="Submit application"
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
        onClose={handleClose}
        closeLabel="Back to plans"
      />
    );
  };

  return (
    <PosCheckoutPageShell
      phase={foreign ? CHECKOUT_PAGE_PHASES.INACTIVE : phase}
      brandName={brandName}
      logoUrl={config?.logoUrl}
      homeHref={`/${clientId}`}
      inactiveMessage="We couldn’t find a subscription checkout in progress for this page. Please go back to your till and choose your plan again."
    >
      {renderCard()}
    </PosCheckoutPageShell>
  );
}

export default PosSubscribeCheckout;
